/**
 * Elections — committee election list, visible to EVERY member (voting and
 * standing are member rights, so this lives outside /admin and has no gate;
 * officers additionally get "New election"). Wired: GET /api/elections.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { goBack, SmallButton } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { useT, monthShort } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import { AppText, Card, EmptyState, Pill, Screen, Skeleton, TopBar } from '@/ui';

export type ElectionRow = {
  id: number; title: string; termLabel: string; seatsTotal: number;
  mode: 'SCEA' | 'SELF_RUN'; status: string; candidateCount: number;
  nominationClose: string | null; votingDate: string | null;
};

export const PHASE_META: Record<string, { labelKey: DictKey; s: 'pending' | 'approved' | 'rejected' | 'urgent' }> = {
  ANNOUNCED: { labelKey: 'el_phase_announced', s: 'pending' },
  NOMINATIONS: { labelKey: 'el_phase_nominations', s: 'urgent' },
  SCRUTINY: { labelKey: 'el_phase_scrutiny', s: 'pending' },
  VOTING: { labelKey: 'el_phase_voting', s: 'urgent' },
  COMPLETED: { labelKey: 'el_phase_completed', s: 'approved' },
  CANCELLED: { labelKey: 'el_phase_cancelled', s: 'rejected' },
};

export function fmtDay(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getDate()} ${monthShort(d.getMonth())} ${d.getFullYear()}`;
}

export default function ElectionsScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const [items, setItems] = React.useState<ElectionRow[]>([]);
  const [canWrite, setCanWrite] = React.useState(false);
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading');

  const load = React.useCallback(async () => {
    setState('loading');
    try {
      const [body, me] = await Promise.all([
        api.get<{ items: ElectionRow[] }>('/api/elections'),
        api.get<{ perms?: Record<string, string[]> }>('/api/positions/me').catch(() => ({ perms: {} as Record<string, string[]> })),
      ]);
      setItems(body.items ?? []);
      setCanWrite((me.perms?.['governance.elections'] ?? []).includes('write'));
      setState('ready');
    } catch {
      setState('error');
    }
  }, []);
  React.useEffect(() => { void Promise.resolve().then(() => load()); }, [load]);

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('el_title')}
        onBack={goBack}
        right={canWrite ? <SmallButton label={t('nav_new')} icon="plus" onPress={() => router.push('/elections/new')} /> : undefined}
      />
      <Screen padded style={{ paddingTop: space.s3 }}>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>{[0, 1].map((i) => <Skeleton key={i} h={96} />)}</View>
        ) : state === 'error' ? (
          <EmptyState
            icon={<Icon name="alert-circle" size={40} color={colors.textTertiary} />}
            title={t('el_err_title')}
            body={t('ct_check_connection')}
            cta={t('retry')}
            onCta={() => { void load(); }}
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={<Icon name="check" size={40} color={colors.textTertiary} />}
            title={t('el_empty_title')}
            body="Committee elections run every 5 years (bye-law 115(a)) — they'll appear here when announced."
          />
        ) : (
          <>
            <AppText variant="bodySm" tone="tertiary" style={{ marginBottom: space.s3 }}>
              {t('el_intro')}
            </AppText>
            <View style={{ gap: space.s3 }}>
              {items.map((e) => {
                const ph = PHASE_META[e.status];
                const parts = [t(e.seatsTotal === 1 ? 'el_seat_one' : 'el_seat_other', { n: e.seatsTotal })];
                if (e.termLabel) parts.push(t('el_term', { t: e.termLabel }));
                parts.push(t(e.candidateCount === 1 ? 'el_cand_one' : 'el_cand_other', { n: e.candidateCount }));
                if (e.votingDate) parts.push(t('el_polls', { d: fmtDay(e.votingDate) }));
                return (
                  <Card key={e.id} onPress={() => router.push(`/elections/${e.id}`)}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s2 }}>
                      <Pill status="pending" label={e.mode === 'SCEA' ? 'SCEA-RUN' : 'SELF-RUN'} />
                      <View style={{ flex: 1 }} />
                      <Pill status={ph?.s ?? 'pending'} label={ph ? t(ph.labelKey) : e.status} />
                    </View>
                    <AppText variant="bodyMd" weight="600" style={{ marginTop: space.s2 }}>{e.title}</AppText>
                    <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                      {parts.join(' · ')}
                    </AppText>
                  </Card>
                );
              })}
            </View>
          </>
        )}
      </Screen>
    </Screen>
  );
}
